import _each from "lodash/each";
import _some from "lodash/some";
import _find from "lodash/find";
import moment from 'moment';
import NwApiSDK from '@nerdwallet/nw-api-sdk';
import { getCategoryWindowRollups as getCategoryWindowRollupsAction, getTopMerchants as getTopMerchantsAction, createMerchantCategoryRule, createTransactionTag, deleteTransactionTag, editTransaction, recategorizeTransaction, updateCategoryWindowRollups, updateTopMerchants, updateFinancialAccountVisibility } from "../actions/yodlee";
import { getAllTransactions, getCategoryWindowRollups, getTopMerchants, getTransactionsToRecategorizeOnMerchantCategoryRule } from "../selectors";
import { yodlee as yodleeUtils } from "../utils";
import { TOP_MERCHANTS_REQUEST_LIMIT } from "../constants";
/**
 * This middleware updates the "categoryWindowRollups" and "topMerchants" reducers
 * (aka "top spending") for all actions that affect these rollups.
 */

export default (function (_ref) {
  var dispatch = _ref.dispatch,
      getState = _ref.getState;
  return function (next) {
    return function (action) {
      var result = next(action);
      var nextState = getState();
      var allTransactions = getAllTransactions(nextState.identity); // If the user recategorizes a transaction, update top spending

      if (action.type === recategorizeTransaction.SUCCESS_TYPE) {
        var _action$meta = action.meta,
            newCategory = _action$meta.category,
            oldCategory = _action$meta.previousCategory,
            transactionId = _action$meta.transactionId;

        var transaction = _find(allTransactions, {
          id: transactionId
        });

        if (transaction) {
          var amount = transaction.amount,
              canonicalMerchantName = transaction.canonical_merchant_name,
              date = transaction.date;
          dispatch(updateCategoryWindowRollups({
            category: oldCategory,
            amount: -amount,
            date: date
          }));
          dispatch(updateCategoryWindowRollups({
            category: newCategory,
            amount: amount,
            date: date
          })); // If moving from spending -> non-spending category, decrease rollup

          if (yodleeUtils.isSpendingTransactionCategory(oldCategory) && !yodleeUtils.isSpendingTransactionCategory(newCategory)) {
            dispatch(updateTopMerchants({
              canonicalMerchantName: canonicalMerchantName,
              amount: -amount,
              date: date
            }));
          } // If moving from non-spending -> spending category, increase rollup


          if (!yodleeUtils.isSpendingTransactionCategory(oldCategory) && yodleeUtils.isSpendingTransactionCategory(newCategory)) {
            dispatch(updateTopMerchants({
              canonicalMerchantName: canonicalMerchantName,
              amount: amount,
              date: date
            }));
          }
        }
      } // If the user edits a transaction, update top spending


      if (action.type === editTransaction.SUCCESS_TYPE) {
        var _action$meta2 = action.meta,
            newAmount = _action$meta2.amount,
            _newCategory = _action$meta2.category,
            newDate = _action$meta2.date,
            previousAmount = _action$meta2.previousAmount,
            previousCategory = _action$meta2.previousCategory,
            previousDate = _action$meta2.previousDate,
            _transactionId = _action$meta2.transactionId;

        var _transaction = _find(allTransactions, {
          id: _transactionId
        });

        if (_transaction) {
          var _canonicalMerchantName = _transaction.canonical_merchant_name;
          var sign = yodleeUtils.isIncomeTransaction(_transaction) ? -1 : 1;
          var newAmountWithSign = newAmount * sign;
          var previousAmountWithSign = previousAmount * sign;
          dispatch(updateCategoryWindowRollups({
            category: previousCategory,
            amount: -previousAmountWithSign,
            date: previousDate
          }));
          dispatch(updateCategoryWindowRollups({
            category: _newCategory,
            amount: newAmountWithSign,
            date: newDate
          }));
          var isPreviousCategorySpending = yodleeUtils.isSpendingTransactionCategory(previousCategory);
          var isNewCategorySpending = yodleeUtils.isSpendingTransactionCategory(_newCategory); // if both the previous and the new category are spending

          if (isPreviousCategorySpending && isNewCategorySpending) {
            dispatch(updateTopMerchants({
              canonicalMerchantName: _canonicalMerchantName,
              amount: -previousAmountWithSign,
              date: previousDate
            }));
            dispatch(updateTopMerchants({
              canonicalMerchantName: _canonicalMerchantName,
              amount: newAmountWithSign,
              date: newDate
            }));
          } // If moving from spending -> non-spending category, decrease rollup
          else if (isPreviousCategorySpending && !isNewCategorySpending) {
              dispatch(updateTopMerchants({
                canonicalMerchantName: _canonicalMerchantName,
                amount: -previousAmountWithSign,
                date: previousDate
              }));
            } // If moving from non-spending -> spending category, increase rollup
            else if (!isPreviousCategorySpending && isNewCategorySpending) {
                dispatch(updateTopMerchants({
                  canonicalMerchantName: _canonicalMerchantName,
                  amount: newAmountWithSign,
                  date: newDate
                }));
              }
        }
      } // If the user hides a transaction from calculations, update top spending


      if (action.type === createTransactionTag.SUCCESS_TYPE || action.type === deleteTransactionTag.SUCCESS_TYPE) {
        var _action$meta3 = action.meta,
            _transactionId2 = _action$meta3.transactionId,
            tag = _action$meta3.tag;

        if (tag === NwApiSDK.IDENTITY_TRANSACTION_TAGS.IGNORED) {
          var _transaction2 = _find(allTransactions, {
            id: _transactionId2
          });

          if (_transaction2) {
            var _amount = _transaction2.amount,
                _canonicalMerchantName2 = _transaction2.canonical_merchant_name,
                category = _transaction2.category,
                _date = _transaction2.date;

            if (yodleeUtils.isSpendingTransactionCategory(category)) {
              // If hiding a transaction, reduce rollup amounts
              if (action.type === createTransactionTag.SUCCESS_TYPE) {
                dispatch(updateCategoryWindowRollups({
                  category: category,
                  amount: -_amount,
                  date: _date
                }));
                dispatch(updateTopMerchants({
                  canonicalMerchantName: _canonicalMerchantName2,
                  amount: -_amount,
                  date: _date
                }));
              } // If un-hiding a transaction, add rollup amounts


              if (action.type === deleteTransactionTag.SUCCESS_TYPE) {
                dispatch(updateCategoryWindowRollups({
                  category: category,
                  amount: _amount,
                  date: _date
                }));
                dispatch(updateTopMerchants({
                  canonicalMerchantName: _canonicalMerchantName2,
                  amount: _amount,
                  date: _date
                }));
              }
            }
          }
        }
      } // If the user recategorizes transactions for all merchant, refetch top spending


      if (action.type === createMerchantCategoryRule.SUCCESS_TYPE) {
        var _action$meta4 = action.meta,
            _canonicalMerchantName3 = _action$meta4.canonicalMerchantName,
            _transactionId3 = _action$meta4.transactionId;
        var transactionsToRecategorize = getTransactionsToRecategorizeOnMerchantCategoryRule(nextState.identity, {
          transactionId: _transactionId3,
          canonicalMerchantName: _canonicalMerchantName3
        }); // For each category and merchant window, check if any transaction date
        // falls in the window, and refetch data if needed

        var isSomeTransactionInWindow = function isSomeTransactionInWindow(transactions, startDate, endDate) {
          return _some(transactions, function (transaction) {
            return moment(transaction.date).isBetween(startDate, endDate, 'day', '[]');
          });
        };

        var categoryWindowRollups = getCategoryWindowRollups(nextState.identity);

        _each(categoryWindowRollups, function (categoryRollups, windowKey) {
          var _yodleeUtils$getRollu = yodleeUtils.getRollupWindowDates(windowKey),
              startDate = _yodleeUtils$getRollu.startDate,
              endDate = _yodleeUtils$getRollu.endDate;

          if (isSomeTransactionInWindow(transactionsToRecategorize, startDate, endDate)) {
            dispatch(getCategoryWindowRollupsAction({
              startDate: startDate,
              endDate: endDate,
              includeSlices: [NwApiSDK.IDENTITY_TRANSACTION_SLICES.WINDOW]
            }));
          }
        });

        var topMerchants = getTopMerchants(nextState.identity);

        _each(topMerchants.window, function (windowData, windowKey) {
          var _yodleeUtils$getRollu2 = yodleeUtils.getRollupWindowDates(windowKey),
              startDate = _yodleeUtils$getRollu2.startDate,
              endDate = _yodleeUtils$getRollu2.endDate;

          if (isSomeTransactionInWindow(transactionsToRecategorize, startDate, endDate)) {
            dispatch(getTopMerchantsAction({
              startDate: startDate,
              endDate: endDate,
              includeSlices: [NwApiSDK.IDENTITY_TRANSACTION_SLICES.WINDOW],
              limit: TOP_MERCHANTS_REQUEST_LIMIT
            }));
          }
        });
      } // If the user hides/shows an account OR if transactions get out of sync,
      // then we need to refetch all the top spending windows


      if (action.type === updateFinancialAccountVisibility.SUCCESS_TYPE || getState().identity.yodlee.transactions.transactionsOutdated) {
        // Update all categoryWindowRollups
        var _categoryWindowRollups = getCategoryWindowRollups(nextState.identity);

        _each(_categoryWindowRollups, function (categoryRollups, windowKey) {
          var _yodleeUtils$getRollu3 = yodleeUtils.getRollupWindowDates(windowKey),
              startDate = _yodleeUtils$getRollu3.startDate,
              endDate = _yodleeUtils$getRollu3.endDate;

          dispatch(getCategoryWindowRollupsAction({
            startDate: startDate,
            endDate: endDate,
            includeSlices: [NwApiSDK.IDENTITY_TRANSACTION_SLICES.WINDOW]
          }));
        }); // Update all topMerchants windows


        var _topMerchants = getTopMerchants(nextState.identity);

        _each(_topMerchants.window, function (windowData, windowKey) {
          var _yodleeUtils$getRollu4 = yodleeUtils.getRollupWindowDates(windowKey),
              startDate = _yodleeUtils$getRollu4.startDate,
              endDate = _yodleeUtils$getRollu4.endDate;

          dispatch(getTopMerchantsAction({
            startDate: startDate,
            endDate: endDate,
            includeSlices: [NwApiSDK.IDENTITY_TRANSACTION_SLICES.WINDOW],
            limit: TOP_MERCHANTS_REQUEST_LIMIT
          }));
        });
      }

      return result;
    };
  };
});